import React, { useCallback, useContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import MUIDataTable from 'mui-datatables';
import AppBarButtonLink from 'components/shared/buttons/AppBarButtonLink';
import LoadingBlock from 'components/shared/blocks/LoadingBlock';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import SessionTypeFilters from 'components/session-type/SessionTypeFilters';
import routes from 'common/routes';
import { getSessionTypeColumns } from 'common/table/columns';
import { getSessionTypeData } from 'common/table/data';
import { defaultTableOptions, getSelectToolBar, tableLocalization } from 'common/table/utils';
import { canModify } from 'common/utils/user';
import AppBarContext from 'context/AppBarContext';
import sessionTypeSlice from 'store/modules/sessionType';

function SessionTypeList() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['buttons', 'labels', 'routes', 'tables']);
  const pageTitle = t('sessionType.list', { ns: 'routes' });
  const emptyMessage = t('empty', { type: pageTitle.toLowerCase(), ns: 'tables' });
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const filters = useSelector((state) => state.sessionType.filters);
  const pagination = useSelector((state) => state.sessionType.pagination);
  const paginatorInfo = useSelector((state) => state.sessionType.paginatorInfo);
  const refresh = useSelector((state) => state.sessionType.refresh);
  const role = useSelector((state) => state.auth.user.role);
  const sessionTypes = useSelector((state) => state.sessionType.sessionTypes);
  const status = useSelector((state) => state.sessionType.status);

  const canModifyData = canModify(role);

  const listSessionTypes = useCallback(() => {
    dispatch(sessionTypeSlice.actions.list({ pagination, filters }));
  }, [dispatch, filters, pagination]);

  useEffect(() => {
    listSessionTypes();
  }, [listSessionTypes, refresh]);

  useEffect(() => {
    setAppBarButton(
      canModifyData ? (
        <AppBarButtonLink label={t('add', { ns: 'buttons' })} path={routes.createSessionType.path} />
      ) : null
    );
    setAppBarTitle(pageTitle);
  }, [canModifyData, pageTitle, setAppBarButton, setAppBarTitle]);

  const tableData = getSessionTypeData(sessionTypes);
  const columns = getSessionTypeColumns({ data: tableData, translateFunc: t });

  const onChangePage = (page) => {
    dispatch(sessionTypeSlice.actions.updatePagination({ page: page + 1 }));
  };

  const onDelete = (ids) => {
    dispatch(sessionTypeSlice.actions.remove(ids));
  };

  const onRestore = (ids) => {
    dispatch(sessionTypeSlice.actions.restore(ids));
  };

  const options = {
    ...defaultTableOptions,
    count: paginatorInfo.total,
    customToolbarSelect: (selectedRows, displayData) =>
      getSelectToolBar({
        filters,
        rows: selectedRows,
        data: displayData,
        onDelete,
        onRestore
      }),
    onChangePage,
    page: pagination.page - 1,
    rowsPerPage: pagination.first,
    selectableRows: canModifyData ? 'multiple' : 'none',
    selectableRowsHeader: canModifyData,
    textLabels: tableLocalization(pageTitle.toLowerCase(), t)
  };

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <SessionTypeFilters />
      <Box
        sx={{
          position: 'relative'
        }}>
        <LoadingBlock
          emptyBlockProps={{ message: emptyMessage }}
          isValid={sessionTypes.length > 0}
          skeletonProps={{ rows: 5 }}
          status={status}
          type="table">
          <MUIDataTable columns={columns} data={tableData} options={options} />
        </LoadingBlock>
      </Box>
    </>
  );
}

export default SessionTypeList;
